import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ThumbsUp, ThumbsDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface VirtualDateActivity {
  id: string;
  room_id: string;
  activity_type: string;
  question_data: Record<string, any> | null;
  user1_response: Record<string, any> | null;
  user2_response: Record<string, any> | null;
  is_complete: boolean;
  created_at: string;
}

interface PartnerProfile {
  id: string;
  name: string;
  photo_url: string | null;
}

interface VirtualDateGameProps {
  activity: VirtualDateActivity;
  partner: PartnerProfile | null;
  isHost: boolean;
  onSubmitResponse: (response: Record<string, any>) => void;
  onComplete: () => void;
}

export const VirtualDateGame = ({
  activity,
  partner,
  isHost,
  onSubmitResponse,
  onComplete,
}: VirtualDateGameProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const myResponse = isHost ? activity.user1_response : activity.user2_response;
  const theirResponse = isHost ? activity.user2_response : activity.user1_response;

  // Auto-show results when both have responded
  useEffect(() => {
    if (myResponse && theirResponse && !showResults) {
      setTimeout(() => setShowResults(true), 500);
    }
  }, [myResponse, theirResponse, showResults]);

  const handleSelect = (option: string) => {
    setSelectedOption(option);
    onSubmitResponse({ choice: option, timestamp: Date.now() });
  };

  // Would You Rather
  if (activity.activity_type === "wouldyourather") {
    const { optionA, optionB } = activity.question_data || {};

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto"
      >
        <div className="text-center mb-6">
          <span className="text-4xl mb-2 block">🤔</span>
          <h3 className="text-xl font-bold text-foreground">Would You Rather...</h3>
        </div>

        <div className="space-y-3">
          {[
            { key: "A", text: optionA },
            { key: "B", text: optionB },
          ].map((option) => {
            const isSelected = myResponse?.choice === option.key;
            const theySelected = theirResponse?.choice === option.key;
            const bothSelected = isSelected && theySelected;

            return (
              <motion.button
                key={option.key}
                whileHover={{ scale: myResponse ? 1 : 1.02 }}
                whileTap={{ scale: myResponse ? 1 : 0.98 }}
                onClick={() => !myResponse && handleSelect(option.key)}
                disabled={!!myResponse}
                className={cn(
                  "w-full p-6 rounded-2xl text-left transition-all relative overflow-hidden",
                  "border-2",
                  !myResponse && "hover:border-primary/50 cursor-pointer",
                  isSelected && "border-primary bg-primary/10",
                  !isSelected && myResponse && "border-border/50 opacity-60",
                  !myResponse && "border-border bg-card"
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-lg">{option.text}</span>
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex items-center gap-2"
                    >
                      <Check className="w-5 h-5 text-primary" />
                    </motion.div>
                  )}
                </div>

                {/* Partner indicator */}
                {showResults && theySelected && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="absolute top-2 right-2"
                  >
                    <Avatar className="w-8 h-8 border-2 border-pink-500">
                      <AvatarImage src={partner?.photo_url || undefined} />
                      <AvatarFallback>{partner?.name?.[0]}</AvatarFallback>
                    </Avatar>
                  </motion.div>
                )}

                {/* Match indicator */}
                {showResults && bothSelected && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute inset-0 flex items-center justify-center bg-primary/20 backdrop-blur-sm"
                  >
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground">
                      <Sparkles className="w-4 h-4" />
                      <span className="font-semibold">You both chose this!</span>
                    </div>
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Waiting indicator */}
        {myResponse && !theirResponse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 text-center"
          >
            <p className="text-muted-foreground flex items-center justify-center gap-2">
              <span className="animate-pulse">⏳</span>
              Waiting for {partner?.name}'s answer...
            </p>
          </motion.div>
        )}

        {/* Results and next */}
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 text-center"
          >
            {myResponse?.choice === theirResponse?.choice ? (
              <p className="text-lg font-semibold text-green-500 mb-4">🎉 Great minds think alike!</p>
            ) : (
              <p className="text-lg font-semibold text-amber-500 mb-4">🤷 Different perspectives!</p>
            )}
            <Button onClick={onComplete} className="gap-2">
              Next Question
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        )}
      </motion.div>
    );
  }

  // Trivia
  if (activity.activity_type === "trivia") {
    const { question, options, answer } = activity.question_data || {};

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto"
      >
        <div className="text-center mb-6">
          <span className="text-4xl mb-2 block">🧠</span>
          <h3 className="text-xl font-bold text-foreground">{question}</h3>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {options?.map((option: string, idx: number) => {
            const isSelected = myResponse?.choice === option;
            const isCorrect = option === answer;
            const showAnswer = showResults;

            return (
              <motion.button
                key={idx}
                whileHover={{ scale: myResponse ? 1 : 1.02 }}
                whileTap={{ scale: myResponse ? 1 : 0.98 }}
                onClick={() => !myResponse && handleSelect(option)}
                disabled={!!myResponse}
                className={cn(
                  "p-4 rounded-xl text-center transition-all border-2",
                  !myResponse && "hover:border-primary/50 cursor-pointer border-border bg-card",
                  isSelected && !showAnswer && "border-primary bg-primary/10",
                  showAnswer && isCorrect && "border-green-500 bg-green-500/10",
                  showAnswer && isSelected && !isCorrect && "border-red-500 bg-red-500/10"
                )}
              >
                <span className="font-medium">{option}</span>
                {showAnswer && isCorrect && <Check className="w-4 h-4 text-green-500 mx-auto mt-1" />}
              </motion.button>
            );
          })}
        </div>

        {myResponse && !theirResponse && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 text-center">
            <p className="text-muted-foreground">Waiting for {partner?.name}...</p>
          </motion.div>
        )}

        {showResults && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 text-center">
            {myResponse?.choice === answer && theirResponse?.choice === answer ? (
              <p className="text-lg font-semibold text-green-500 mb-4">🎉 You both got it right!</p>
            ) : myResponse?.choice === answer ? (
              <p className="text-lg font-semibold text-amber-500 mb-4">✨ You got it!</p>
            ) : theirResponse?.choice === answer ? (
              <p className="text-lg font-semibold text-amber-500 mb-4">{partner?.name} got it!</p>
            ) : (
              <p className="text-lg font-semibold text-red-400 mb-4">Neither got it 😅</p>
            )}
            <Button onClick={onComplete} className="gap-2">
              Next Question
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        )}
      </motion.div>
    );
  }

  // Truth or Dare
  if (activity.activity_type === "truth_or_dare") {
    const { truth, dare } = activity.question_data || {};
    const myChoice = myResponse?.choice;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto"
      >
        <div className="text-center mb-6">
          <span className="text-4xl mb-2 block">🎭</span>
          <h3 className="text-xl font-bold text-foreground">Truth or Dare?</h3>
        </div>

        {!myChoice ? (
          <div className="grid grid-cols-2 gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSelect("truth")}
              className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-2 border-blue-500/30 hover:border-blue-500 transition-colors"
            >
              <span className="text-3xl block mb-2">💭</span>
              <span className="font-bold text-lg">Truth</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSelect("dare")}
              className="p-8 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border-2 border-orange-500/30 hover:border-orange-500 transition-colors"
            >
              <span className="text-3xl block mb-2">🔥</span>
              <span className="font-bold text-lg">Dare</span>
            </motion.button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 rounded-2xl bg-card border border-border"
          >
            <p className="text-sm text-muted-foreground mb-2">
              You chose {myChoice === "truth" ? "💭 Truth" : "🔥 Dare"}
            </p>
            <p className="text-xl font-medium text-foreground">
              {myChoice === "truth" ? truth : dare}
            </p>

            <div className="mt-6 flex gap-3">
              <Button variant="outline" className="flex-1 gap-2" onClick={onComplete}>
                <ThumbsDown className="w-4 h-4" />
                Skip
              </Button>
              <Button className="flex-1 gap-2" onClick={onComplete}>
                <ThumbsUp className="w-4 h-4" />
                Done!
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  }

  // Default fallback
  return (
    <div className="text-center py-12">
      <p className="text-muted-foreground">Game loading...</p>
      <Button onClick={onComplete} className="mt-4">
        Skip
      </Button>
    </div>
  );
};
